import type { TFunction } from "i18next";

/**
 * Maps the backend's import-reject reasons (a closed set of English strings
 * emitted by ItemsCsvParser / ExpectedStockCsvParser / StockMovementCsvParser /
 * ZonesJson) to i18n keys so the report reads in the UI language. Client-side
 * on purpose: the reasons are already persisted as strings in historical jsonb
 * reports, so a backend reason-code retrofit would still leave old rows
 * untranslated (ADR 138).
 *
 * Unknown reasons return null — callers show the raw backend string, which is
 * still honest, just untranslated.
 */
export interface RejectReasonKey {
  key: string;
  params?: Record<string, string>;
}

const FIXED: Record<string, string> = {
  "non-integer device": "inventory.reject.device",
  "non-integer quantity": "inventory.reject.quantity",
  "non-numeric quantity": "inventory.reject.quantity",
  "non-integer mode": "inventory.reject.mode",
  "unparseable timestamp": "inventory.reject.timestamp",
  "unparseable AsOf date": "inventory.reject.asOf",
  "null zone entry skipped": "inventory.reject.zoneNull",
  "zone with blank id skipped": "inventory.reject.zoneBlankId",
  // Movements feed (ADR 377) — StockMovementCsvParser names the CSV column
  // (capitalised), so its reasons are distinct strings from the older parsers'.
  "unparseable Timestamp; expected yyyy-MM-dd HH:mm:ss": "inventory.reject.movementTimestamp",
  "Timestamp carries an offset; expected a naive wall clock on the site's timezone": "inventory.reject.movementOffset",
  "Timestamp out of range": "inventory.reject.movementRange",
  "blank Product": "inventory.reject.blankProduct",
  "non-numeric Quantity": "inventory.reject.quantity",
  "Quantity must be > 0": "inventory.reject.quantityPositive",
  "adjust Quantity must be non-zero": "inventory.reject.adjustNonZero",
};

export function rejectReasonKey(reason: string): RejectReasonKey | null {
  const fixed = FIXED[reason];
  if (fixed) return { key: fixed };

  let m = /^expected (\d+) columns, got (\d+)$/.exec(reason);
  if (m) return { key: "inventory.reject.columns", params: { expected: m[1], got: m[2] } };

  m = /^expected (\d+) or (\d+) columns, got (\d+)$/.exec(reason);
  if (m) return { key: "inventory.reject.columnsRange", params: { min: m[1], max: m[2], got: m[3] } };

  m = /^expected (\d+) to (\d+) columns, got (\d+)$/.exec(reason);
  if (m) return { key: "inventory.reject.columnsBetween", params: { min: m[1], max: m[2], got: m[3] } };

  m = /^unknown Type '(.*)'; expected pick\|issue\|receipt\|transfer\|adjust$/.exec(reason);
  if (m) return { key: "inventory.reject.movementType", params: { type: m[1] } };

  m = /^duplicate key \(address\+product already seen\): (.*)$/.exec(reason);
  if (m) return { key: "inventory.reject.duplicate", params: { key: m[1] } };

  m = /^(.+): polygon has fewer than 3 points$/.exec(reason);
  if (m) return { key: "inventory.reject.zonePolygon", params: { id: m[1] } };

  return null;
}

/** Translated reason when the string is recognized; the raw backend string otherwise. */
export function rejectReasonLabel(reason: string, t: TFunction): string {
  const mapped = rejectReasonKey(reason);
  return mapped ? t(mapped.key, mapped.params) : reason;
}
